import React, { useState, useEffect } from 'react';
import { crmApi } from '../services/crmApi';
import { DocumentStudio } from './detail/DocumentStudio';

interface SmartDocType {
  id: string;
  label: string;
  category: string;
  description: string;
  product?: string;
}

interface ApplicationData {
  first_name: string;
  last_name: string;
  pesel: string;
  phone: string;
  email: string;
  residence_address: string;
  premises_address: string;
  business_idea_raw: string;
  pkd_code: string;
  industry: string;
  service_type?: string;
  [key: string]: any;
}

interface Props {
  applicationId: number;
  businessIdeaRaw?: string;
  currentBusinessTypeId?: number;
  serviceType?: string;
}

// Category grouping config
const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  listy_intencyjne: { label: 'Deklaracje współpracy', color: 'text-blue-800', bg: 'bg-blue-50', border: 'border-blue-200' },
  referencje: { label: 'Referencje', color: 'text-green-800', bg: 'bg-green-50', border: 'border-green-200' },
  oswiadczenia: { label: 'Oświadczenia', color: 'text-purple-800', bg: 'bg-purple-50', border: 'border-purple-200' },
  zaswiadczenia: { label: 'Zaświadczenia', color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-200' },
  wnioski: { label: 'Wnioski', color: 'text-indigo-800', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  szkolenia: { label: 'Szkolenia', color: 'text-teal-800', bg: 'bg-teal-50', border: 'border-teal-200' },
  raporty: { label: 'Raporty', color: 'text-orange-800', bg: 'bg-orange-50', border: 'border-orange-200' },
  pisma: { label: 'Pisma urzędowe', color: 'text-red-800', bg: 'bg-red-50', border: 'border-red-200' },
  certyfikaty: { label: 'Certyfikaty', color: 'text-emerald-800', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  formularze: { label: 'Formularze', color: 'text-cyan-800', bg: 'bg-cyan-50', border: 'border-cyan-200' },
  umowy: { label: 'Umowy i dokumenty', color: 'text-slate-800', bg: 'bg-slate-50', border: 'border-slate-200' },
  oferty: { label: 'Oferty i propozycje', color: 'text-violet-800', bg: 'bg-violet-50', border: 'border-violet-200' },
  projekty: { label: 'Dokumenty projektówe', color: 'text-pink-800', bg: 'bg-pink-50', border: 'border-pink-200' },
  rozliczenia: { label: 'Rozliczenia', color: 'text-lime-800', bg: 'bg-lime-50', border: 'border-lime-200' },
  karty: { label: 'Karty i ewidencje', color: 'text-fuchsia-800', bg: 'bg-fuchsia-50', border: 'border-fuchsia-200' },
  listy: { label: 'Listy', color: 'text-sky-800', bg: 'bg-sky-50', border: 'border-sky-200' },
  protokoly: { label: 'Protokoły', color: 'text-stone-800', bg: 'bg-stone-50', border: 'border-stone-200' },
};

// Fields needed per document type (PUP legacy)
const FIELDS_FOR_TYPE: Record<string, string[]> = {
  declaration_private: ['counterpartyName', 'counterpartyAddress', 'serviceDescription'],
  declaration_company: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyNip', 'counterpartyPosition', 'serviceDescription'],
  declaration_wholesaler: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyNip', 'serviceDescription'],
  reference_salon: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyPosition', 'serviceDescription'],
  reference_private: ['counterpartyName', 'counterpartyAddress', 'serviceDescription'],
  spouse_consent: ['spouseFirstName', 'spouseLastName', 'spousePesel', 'serviceDescription'],
  internship_certificate: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyPosition', 'internshipPeriod', 'internshipHours', 'serviceDescription'],
  // BHP / ppoż
  bhp_initial_cert: ['participantName', 'position', 'positionType', 'topic', 'hours', 'trainingDate', 'instructor'],
  bhp_periodic_cert: ['participantName', 'position', 'positionType', 'topic', 'hours', 'trainingDate', 'validYears', 'instructor'],
  bhp_initial_card: ['participantName', 'position', 'department', 'generalDate', 'generalInstructor', 'workstation', 'stationDateFrom', 'stationDateTo', 'stationInstructor'],
  ppoz_cert: ['participantName', 'position', 'topic', 'hours', 'trainingDate', 'instructor'],
  bhp_risk_assessment: ['position', 'tasks', 'method', 'measures', 'residualRisk', 'assessmentDate', 'assessors'],
  bhp_service_offer: ['scope', 'headcount', 'priceNote', 'validity'],
  ppoz_ibp: ['objectName', 'objectAddress', 'objectPurpose', 'zlCategory', 'floorsArea', 'maxOccupancy', 'extinguishers', 'evacRoutes', 'assemblyPoint', 'author'],
  ppoz_evac_protocol: ['objectName', 'objectScope', 'evacDate', 'evacTime', 'evacuatedCount', 'evacDuration', 'alarmMethod', 'observations', 'issues', 'recommendations', 'instructor'],
};

const FIELD_LABELS: Record<string, { label: string; placeholder: string }> = {
  counterpartyName: { label: 'Imię i nazwisko kontrahenta', placeholder: 'np. Anna Nowak' },
  counterpartyAddress: { label: 'Adres kontrahenta', placeholder: 'np. ul. Kwiatowa 5, 30-000 Kraków' },
  counterpartyCompany: { label: 'Nazwa firmy/salonu', placeholder: 'np. Salon Urody "Bella"' },
  counterpartyNip: { label: 'NIP firmy', placeholder: 'np. 1234567890' },
  counterpartyPosition: { label: 'Stanowisko', placeholder: 'np. Właściciel' },
  spouseFirstName: { label: 'Imię małżonka/małżonki', placeholder: 'np. Maria' },
  spouseLastName: { label: 'Nazwisko małżonka/małżonki', placeholder: 'np. Kowalska' },
  spousePesel: { label: 'PESEL małżonka/małżonki', placeholder: 'np. 85010112345' },
  serviceDescription: { label: 'Opis usługi (opcjonalnie - auto z CRM)', placeholder: 'np. usługi kosmetyczne, manicure i pedicure' },
  internshipPeriod: { label: 'Okres praktyk', placeholder: 'np. 01.01.2025 - 31.03.2025' },
  internshipHours: { label: 'Liczba godzin', placeholder: 'np. 480 godzin' },
  // BHP / ppoż
  participantName: { label: 'Imię i nazwisko uczestnika', placeholder: 'np. Jan Kowalski' },
  position: { label: 'Stanowisko', placeholder: 'np. operator maszyn' },
  positionType: { label: 'Grupa stanowisk', placeholder: 'robotniczych / administracyjno-biurowych / kierowniczych' },
  topic: { label: 'Temat / zakres szkolenia', placeholder: 'np. BHP dla pracowników na stanowiskach robotniczych' },
  hours: { label: 'Liczba godzin', placeholder: 'np. 8' },
  trainingDate: { label: 'Data szkolenia', placeholder: 'np. 24.07.2026' },
  validYears: { label: 'Ważność (lata)', placeholder: '1 / 3 / 5 (wg stanowiska)' },
  instructor: { label: 'Osoba prowadząca', placeholder: 'Sebastian Adamczyk' },
  department: { label: 'Komórka organizacyjna', placeholder: 'np. Dział produkcji' },
  generalDate: { label: 'Data instruktażu ogólnego', placeholder: 'np. 24.07.2026' },
  generalInstructor: { label: 'Prowadzący instruktaż ogólny', placeholder: 'Sebastian Adamczyk' },
  workstation: { label: 'Stanowisko (instruktaż stanowiskowy)', placeholder: 'np. linia montażowa' },
  stationDateFrom: { label: 'Instruktaż stanowiskowy — od', placeholder: 'np. 24.07.2026' },
  stationDateTo: { label: 'Instruktaż stanowiskowy — do', placeholder: 'np. 26.07.2026' },
  stationInstructor: { label: 'Prowadzący instruktaż stanowiskowy', placeholder: 'np. kierownik działu' },
  tasks: { label: 'Opis stanowiska / zadań', placeholder: 'krótki opis wykonywanych czynności' },
  method: { label: 'Metoda oceny', placeholder: 'Risk Score (R = P × E × S)' },
  measures: { label: 'Środki profilaktyczne', placeholder: 'np. środki ochrony indywidualnej, procedury' },
  residualRisk: { label: 'Ryzyko szczątkowe', placeholder: 'akceptowalne / małe / średnie / duże' },
  assessmentDate: { label: 'Data oceny', placeholder: 'np. 24.07.2026' },
  assessors: { label: 'Zespół oceniający', placeholder: 'Sebastian Adamczyk (specjalista BHP)' },
  scope: { label: 'Zakres usług (oferta)', placeholder: 'np. szkolenia okresowe + ocena ryzyka + IBP' },
  headcount: { label: 'Liczba osób / stanowisk', placeholder: 'np. 25' },
  priceNote: { label: 'Uwaga cenowa', placeholder: 'wycena indywidualna / po ustaleniu zakresu' },
  validity: { label: 'Ważność oferty', placeholder: 'np. 30 dni' },
  // ppoż: IBP + ewakuacja
  objectName: { label: 'Nazwa obiektu', placeholder: 'np. Budynek biurowy Alfa' },
  objectAddress: { label: 'Adres obiektu', placeholder: 'np. ul. Przykładowa 1, Kraków' },
  objectPurpose: { label: 'Przeznaczenie obiektu', placeholder: 'np. biurowo-usługowe' },
  zlCategory: { label: 'Kategoria ZL', placeholder: 'np. ZL III' },
  floorsArea: { label: 'Kondygnacje / powierzchnia', placeholder: 'np. 3 kondygnacje, 1200 m²' },
  maxOccupancy: { label: 'Maks. liczba osób', placeholder: 'np. 80' },
  extinguishers: { label: 'Gaśnice (rodzaj/ilość)', placeholder: 'np. 6× GP-4x ABC, przy wyjściach' },
  evacRoutes: { label: 'Drogi ewakuacyjne', placeholder: 'np. 2 klatki schodowe, wyjścia na parterze' },
  assemblyPoint: { label: 'Miejsce zbiórki', placeholder: 'np. parking przed budynkiem' },
  author: { label: 'Opracował', placeholder: 'Sebastian Adamczyk' },
  objectScope: { label: 'Obiekt / strefa ćwiczenia', placeholder: 'np. cały budynek / II piętro' },
  evacDate: { label: 'Data ćwiczenia', placeholder: 'np. 25.07.2026' },
  evacTime: { label: 'Godzina', placeholder: 'np. 10:00' },
  evacuatedCount: { label: 'Liczba ewakuowanych', placeholder: 'np. 45' },
  evacDuration: { label: 'Czas ewakuacji', placeholder: 'np. 3 min 20 s' },
  alarmMethod: { label: 'Sposób ogłoszenia alarmu', placeholder: 'np. sygnał dźwiękowy / komunikat głosowy' },
  observations: { label: 'Obserwacje z przebiegu', placeholder: 'krótki opis przebiegu ćwiczenia' },
  issues: { label: 'Nieprawidłowości', placeholder: 'np. nie stwierdzono' },
  recommendations: { label: 'Zalecenia', placeholder: 'np. doposażyć oznakowanie na II piętrze' },
};

// Product labels for display
const PRODUCT_LABELS: Record<string, string> = {
  pup_grant: 'Dotacje PUP',
  wsparcie_na_starcie: 'Wsparcie na Starcie',
  kfs: 'KFS — Krajowy Fundusz Szkoleniowy',
  dotacje_rozwojowe: 'Dotacje na Rozwój',
  turnusy: 'Turnusy Rehabilitacyjne',
  wycieczki: 'Wycieczki',
  zus: 'Pomoc ZUS',
  bhp: 'Szkolenia BHP',
  czyste_powietrze: 'Czyste Powietrze',
  hutnik_biznes: 'Hutnik Business',
  marketing_beauty: 'Marketing / B2B',
  gastro_consulting: 'Gastro / B2B',
  uslugi_ksiegowe: 'Usługi Księgowe',
  granty_fpn: 'Granty FPN',
};

export function DocumentGeneratorPanel({
  applicationId,
  serviceType,
}: Props) {
  const [docTypes, setDocTypes] = useState<SmartDocType[]>([]);
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [additionalData, setAdditionalData] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [studioDocType, setStudioDocType] = useState<string | null>(null);
  const [studioDocLabel, setStudioDocLabel] = useState('');

  useEffect(() => {
    loadData();
  }, [applicationId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [appData] = await Promise.all([
        crmApi.getApplication(applicationId),
      ]);
      setApplicationData(appData as ApplicationData);

      // Get product code from app or prop
      const productCode = serviceType || (appData as ApplicationData).service_type || 'pup_grant';
      const types = await crmApi.getProductDocTypes(productCode);
      setDocTypes(types);
    } catch (err) {
      console.error('Error loading document generator data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (typeId: string) => {
    // Open Document Studio for smart doc types, fallback to old modal for others
    const dt = docTypes.find(d => d.id === typeId);
    const SMART_TYPES = ['declaration_private', 'declaration_company', 'declaration_wholesaler', 'reference_salon', 'reference_private', 'spouse_consent', 'internship_certificate'];
    if (SMART_TYPES.includes(typeId)) {
      setStudioDocType(typeId);
      setStudioDocLabel(dt?.label || typeId);
      return;
    }
    setSelectedType(typeId);
    setAdditionalData({});
    setError(null);
    setSuccessMsg(null);
  };

  const closeModal = () => {
    setSelectedType(null);
    setAdditionalData({});
    setError(null);
    setSuccessMsg(null);
  };

  const handleGenerate = async (format: 'pdf' | 'docx') => {
    if (!selectedType) return;
    setGenerating(format);
    setError(null);
    setSuccessMsg(null);

    try {
      const response = await crmApi.generateProductDocument(applicationId, selectedType, additionalData, format);

      // Download file
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const disposition = response.headers?.['content-disposition'];
      let filename = `Dokument_${Date.now()}.${format}`;
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccessMsg(`Dokument ${format.toUpperCase()} wygenerowany i pobrany!`);
    } catch (err: any) {
      console.error('Error generating document:', err);
      let errorMessage = 'Błąd podczas generowania dokumentu';

      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.details || errorMessage;
        } catch {
          // blob parsing failed
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setError(errorMessage);
    } finally {
      setGenerating(null);
    }
  };

  // AI document generation
  const [aiContent, setAiContent] = useState<string | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);

  const handleGenerateAI = async () => {
    if (!selectedType) return;
    setGeneratingAI(true);
    setError(null);
    setAiContent(null);
    try {
      const result = await crmApi.generateAIDocument(applicationId, selectedType, additionalData['aiContext'] || '');
      setAiContent(result.content);
      setSuccessMsg('Dokument AI wygenerowany!');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Blad generowania AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  // Group doc types by category
  const groupedTypes: Record<string, SmartDocType[]> = {};
  for (const dt of docTypes) {
    if (!groupedTypes[dt.category]) groupedTypes[dt.category] = [];
    groupedTypes[dt.category].push(dt);
  }

  const selectedDocType = docTypes.find(d => d.id === selectedType);
  const fieldsForSelected = selectedType ? (FIELDS_FOR_TYPE[selectedType] || []) : [];
  const productCode = serviceType || applicationData?.service_type || 'pup_grant';
  const productLabel = PRODUCT_LABELS[productCode] || productCode;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-500">Ładowanie generatora dokumentów...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">
          Generator Dokumentów
        </h2>
        <span className="text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-medium">
          {productLabel}
        </span>
      </div>

      {applicationData && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
          <span className="font-medium">{applicationData.first_name} {applicationData.last_name}</span>
          {applicationData.industry && <span className="text-gray-500 ml-2">| {applicationData.industry}</span>}
          {applicationData.pkd_code && <span className="text-gray-500 ml-2">| PKD: {applicationData.pkd_code}</span>}
          <p className="text-xs text-gray-400 mt-1">Dane z CRM - automatycznie wstawiane do dokumentów</p>
        </div>
      )}

      {docTypes.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg mb-2">Brak dostępnych typów dokumentów</p>
          <p className="text-sm">Dla produktu <strong>{productLabel}</strong> nie skonfigurowano jeszcze generatorów dokumentów.</p>
        </div>
      )}

      {/* Document types grouped by category */}
      {Object.entries(groupedTypes).map(([category, types]) => {
        const cfg = CATEGORY_CONFIG[category] || { label: category, color: 'text-gray-800', bg: 'bg-gray-50', border: 'border-gray-200' };
        return (
          <div key={category} className="space-y-2">
            <h3 className={`text-lg font-semibold ${cfg.color}`}>{cfg.label}</h3>
            <div className="grid gap-2">
              {types.map((dt) => (
                <div
                  key={dt.id}
                  className={`border ${cfg.border} ${cfg.bg} rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => openModal(dt.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{dt.label}</h4>
                      <p className="text-sm text-gray-600 mt-1">{dt.description}</p>
                    </div>
                    <button
                      className="ml-4 bg-[#1e3a5f] hover:bg-[#2a4f7a] text-white px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap"
                      onClick={(e) => { e.stopPropagation(); openModal(dt.id); }}
                    >
                      Generuj
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Modal */}
      {selectedType && selectedDocType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-1">
              {selectedDocType.label}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {selectedDocType.description}
            </p>

            {/* CRM data preview */}
            {applicationData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
                <p className="font-medium text-blue-900 mb-1">Dane z CRM (automatyczne):</p>
                <div className="grid grid-cols-2 gap-1 text-blue-800">
                  <span>Wnioskodawca: {applicationData.first_name} {applicationData.last_name}</span>
                  <span>PESEL: {applicationData.pesel || '-'}</span>
                  <span>Adres: {applicationData.premises_address || applicationData.residence_address || '-'}</span>
                  <span>PKD: {applicationData.pkd_code || '-'}</span>
                </div>
              </div>
            )}

            {/* Additional fields for this document type */}
            {fieldsForSelected.length > 0 && (
              <div className="space-y-3 mb-4">
                <p className="text-sm font-medium text-gray-700">Dane dodatkowe (kontrahent/osoba trzecia):</p>
                {fieldsForSelected.map((field) => {
                  const meta = FIELD_LABELS[field] || { label: field, placeholder: '' };
                  return (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-600 mb-1">{meta.label}</label>
                      {field === 'serviceDescription' ? (
                        <textarea
                          value={additionalData[field] || ''}
                          onChange={(e) => setAdditionalData({ ...additionalData, [field]: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#1e3a5f] focus:border-[#1e3a5f] text-sm"
                          placeholder={meta.placeholder}
                          rows={2}
                        />
                      ) : (
                        <input
                          type="text"
                          value={additionalData[field] || ''}
                          onChange={(e) => setAdditionalData({ ...additionalData, [field]: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#1e3a5f] focus:border-[#1e3a5f] text-sm"
                          placeholder={meta.placeholder}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Error / Success */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
                {error}
              </div>
            )}
            {successMsg && (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 text-sm">
                {successMsg}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 justify-between items-center pt-4 border-t">
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-sm"
              >
                Zamknij
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleGenerateAI}
                  disabled={!!generating || generatingAI}
                  className="px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center gap-1"
                >
                  {generatingAI ? (
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : null}
                  AI Treść
                </button>
                <button
                  onClick={() => handleGenerate('pdf')}
                  disabled={!!generating}
                  className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center gap-1"
                >
                  {generating === 'pdf' ? (
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : null}
                  PDF
                </button>
                <button
                  onClick={() => handleGenerate('docx')}
                  disabled={!!generating}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center gap-1"
                >
                  {generating === 'docx' ? (
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : null}
                  DOCX
                </button>
              </div>
            </div>

            {/* AI Context input */}
            <div className="mt-3">
              <label className="text-xs font-medium text-gray-600">Dodatkowy kontekst AI (opcjonalnie)</label>
              <textarea
                value={additionalData['aiContext'] || ''}
                onChange={e => setAdditionalData(prev => ({ ...prev, aiContext: e.target.value }))}
                className="w-full mt-1 p-2 border rounded-lg text-sm resize-none"
                rows={2}
                placeholder="Np. dodatkowe wymagania, szczegóły, ton dokumentu..."
              />
            </div>

            {/* AI Content Preview */}
            {aiContent && (
              <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-purple-800 text-sm">Treść wygenerowana przez AI</h4>
                  <button
                    onClick={() => { navigator.clipboard.writeText(aiContent); setSuccessMsg('Skopiowano!'); }}
                    className="text-xs text-purple-600 hover:text-purple-800"
                  >Kopiuj</button>
                </div>
                <div className="prose prose-sm max-h-[400px] overflow-y-auto text-gray-800 text-xs leading-relaxed whitespace-pre-wrap">
                  {aiContent}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Jak to działa?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- <strong>Dane z CRM:</strong> Imię, nazwisko, PESEL, adres, branża - pobierane automatycznie</li>
          <li>- <strong>Pan/Pani:</strong> System automatycznie rozpoznaje płeć i odmienia</li>
          <li>- <strong>PDF + DOCX + Drukuj:</strong> Podgląd inline, edytor WYSIWYG, eksport</li>
          <li>- <strong>Dedykowane szablony:</strong> Dokumenty dopasowane do produktu: {productLabel}</li>
        </ul>
      </div>

      {/* Document Studio */}
      {studioDocType && (
        <DocumentStudio
          applicationId={applicationId}
          docType={studioDocType}
          docLabel={studioDocLabel}
          onClose={() => setStudioDocType(null)}
        />
      )}
    </div>
  );
}
